const makeMemoryStore = () => {
  const mem = new Map();
  return {
    getItem: (k) => (mem.has(k) ? mem.get(k) : null),
    setItem: (k, v) => { mem.set(k, String(v)); },
    removeItem: (k) => { mem.delete(k); },
    clear: () => { mem.clear(); },
    key: (i) => Array.from(mem.keys())[i] ?? null,
    get length() { return mem.size; },
    type: "memory",
    persistent: false,
  };
};

const getBrowserStorage = (storageName) => {
  if (typeof window === "undefined") return null;

  try {
    return window[storageName] || null;
  } catch {
    return null;
  }
};

const isPersistentStorageAvailable = (storageName) => {
  if (typeof window === "undefined") return false;

  try {
    const storage = getBrowserStorage(storageName);
    if (!storage) return false;

    const key = `__ll_storage_check__${Date.now()}_${Math.random()}`;
    const value = "1";
    storage.setItem(key, value);
    const storedValue = storage.getItem(key);
    storage.removeItem(key);
    return storedValue === value;
  } catch {
    return false;
  }
};

const createSafeBrowserStorage = (storageName) => {
  if (typeof window === "undefined") return makeMemoryStore();

  const real = getBrowserStorage(storageName);
  if (!real) return makeMemoryStore();

  try {
    const probe = `__ll_${storageName}_probe__`;
    real.setItem(probe, "1");
    real.removeItem(probe);

    const mem = new Map();

    const mergedKeys = () => {
      const keys = new Set();
      try {
        for (let i = 0; i < real.length; i += 1) {
          const k = real.key(i);
          if (k != null) keys.add(k);
        }
      } catch {}
      for (const k of mem.keys()) keys.add(k);
      return Array.from(keys);
    };

    return {
      getItem: (k) => {
        if (mem.has(k)) return mem.get(k);
        try {
          return real.getItem(k);
        } catch {
          return null;
        }
      },
      setItem: (k, v) => {
        try {
          real.setItem(k, String(v));
        } catch {
          // Quota/security error → fall back silently to memory
          mem.set(k, String(v));
        }
      },
      removeItem: (k) => {
        try { real.removeItem(k); } catch {}
        mem.delete(k);
      },
      clear: () => {
        try { real.clear(); } catch {}
        mem.clear();
      },
      key: (i) => mergedKeys()[i] ?? null,
      get length() { return mergedKeys().length; },
      // Non-standard metadata
      type: "hybrid",
      persistent: true, // has browser storage backing; failed writes still fall back to memory
    };
  } catch {
    return makeMemoryStore();
  }
};

export const isPersistentLocalStorageAvailable = () =>
  isPersistentStorageAvailable("localStorage");

export const isPersistentSessionStorageAvailable = () =>
  isPersistentStorageAvailable("sessionStorage");

export const safeLocalStorage = createSafeBrowserStorage("localStorage");

export const safeSessionStorage = createSafeBrowserStorage("sessionStorage");
